import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Grid,
  CircularProgress,
  Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Add, Remove } from '@mui/icons-material';
import { DatePicker,TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Editor } from '@tinymce/tinymce-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { productDict, productLogo } from "../constants/products";
// import CampaignScheduler from "./UpdateCampaignSchedule";
import { 
  // fetchRecentCampaigns, 
  scheduleCampaign } from "../services/apiServices";



const API_BASE_URL = process.env.REACT_APP_FASTAPI_URL;
const REACT_BASE_URL = process.env.REACT_APP_REDIRECT_URI;

const emailTypes = [
  { value: 'initial', label: 'Initial Email' },
  { value: 'opened_followup', label: 'Follow-up (Opened)', defaultPrompt: "Craft an engaging follow-up email for recipients who opened the initial email. Focus on addressing their interest and encouraging action." },
  { value: 'not_opened_followup', label: 'Follow-up (Not Opened)', defaultPrompt: "Create a compelling follow-up email for recipients who did not open the first email. Make it attention-grabbing and highlight key benefits." },
  { value: 'clicked_followup', label: 'Follow-up (Clicked)', defaultPrompt: "Generate a follow-up email for recipients who clicked links in the initial email. Provide next steps, incentives, or deeper insights." },
];

const DripScheduleForm = () => {
  const [selectedProduct, setSelectedProduct] = useState('');
  const [schedules, setSchedules] = useState([{ step: 1, sendDate: null, extraPrompt: '', emailType: 'initial' }]);
  const [mailingLists, setMailingLists] = useState([]);
  const [selectedListKey, setSelectedListKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatedTemplates, setGeneratedTemplates] = useState([]);
  const [sendTime, setSendTime] = useState(null);

  const resetForm = () => {
    setGeneratedTemplates([]);
    setSelectedProduct('');
    setSchedules([{ step: 1, sendDate: null, extraPrompt: '', emailType: 'initial' }]);
    // keep mailing lists so user doesn’t need to re-fetch
    setSelectedListKey(mailingLists[0]?.listkey || '');
    setSendTime(null);
  };

  
  useEffect(() => {
    async function fetchMailingLists() {
      try {
        const res = await axios.post(`${API_BASE_URL}/emailgenerator/mailinglists`);
        if (res.data.list_of_details) {
          setMailingLists(res.data.list_of_details);
          if (res.data.list_of_details.length > 0) setSelectedListKey(res.data.list_of_details[0].listkey);
        }
      } catch (error) {
        toast.error("Failed to fetch mailing lists");
      }
    }
    fetchMailingLists();
  }, []);

  const handleAddSchedule = () => {
    setSchedules([...schedules, { step: schedules.length + 1, sendDate: null, extraPrompt: '', emailType: 'initial' }]);
  };

  const handleRemoveSchedule = (index) => {
    if (schedules.length === 1) return;
    const updated = schedules.filter((_, i) => i !== index);
    updated.forEach((item, idx) => (item.step = idx + 1));
    setSchedules(updated);
  };

  const handleScheduleChange = (index, field, value) => {
    const updated = [...schedules];
    updated[index] = { ...updated[index], [field]: value };
    // auto-fill extra prompt if field is emailType and prompt is empty
    if (field === 'emailType') {
      const defaultPrompt = emailTypes.find(e => e.value === value)?.defaultPrompt || '';
      updated[index].extraPrompt = defaultPrompt;
    }
    setSchedules(updated);
  };

  const validateForm = () => {
    if (!selectedProduct) { toast.error('Please select a product.'); return false; }
    // if (!selectedListKey) { toast.error('Please select a mailing list.'); return false; }
    for (const sched of schedules) {
      if (!sched.sendDate) { toast.error('Please select send date for all steps.'); return false; }
    }
    return true;
  };

  const handleGenerateAllTemplates = async () => {
    if (!validateForm()) return;
    setLoading(true);
    const tempTemplates = [];
    for (let i = 0; i < schedules.length; i++) {
      const sched = schedules[i];
      const prompt = sched.extraPrompt?.trim() || (emailTypes.find(t => t.value === sched.emailType)?.defaultPrompt || '');
      try {
        const res = await axios.post(`${API_BASE_URL}/emailgenerator/generateemail`, {
          product_name: selectedProduct,
          product_url: productDict[selectedProduct],
          product_logo: productLogo[selectedProduct],
          extra_prompt: prompt,
        });
        tempTemplates.push({
          product_name: selectedProduct,
          product_url: productDict[selectedProduct],
          product_logo: productLogo[selectedProduct],
          sendDate: sched.sendDate,
          sendTime: sendTime, // ✅ add time
          step: sched.step,
          templateName: res.data.template_name || '',
          subject: res.data.subject || '',
          preheader: res.data.preheader || '',
          htmlBody: res.data.html_body || '',
          emailType: sched.emailType,
          extraPrompt: prompt,
        });
        
      } catch (error) {
        toast.error(`Failed to generate template for Step ${sched.step}`);
      }
    }
    setGeneratedTemplates(tempTemplates);
    setLoading(false);
  };

  return (
    <Box sx={{ p: 2, minHeight:'80vh' }}>
       {/* <CampaignScheduler
        fetchCampaigns={fetchRecentCampaigns}
        scheduleCampaign={scheduleCampaign}
      /> */}
      <Typography variant="h5" mb={2}>Multi-Step Drip Campaign</Typography>

{generatedTemplates.length <= 0 && <>
      {/* Product selection */}
      <FormControl fullWidth margin="normal">
        <InputLabel id="product-select-label">Select Product</InputLabel>
        <Select
          labelId="product-select-label"
          value={selectedProduct}
          label="Select Product"
          onChange={(e) => setSelectedProduct(e.target.value)}
          renderValue={(selected) => selected ? (
            <Grid container alignItems="center" spacing={1}>
              <Grid item><Box component="img" src={productLogo[selected]} alt={selected} sx={{ width: 124, height: 14, objectFit: "contain" }} /></Grid>
              <Grid item>{selected}</Grid>
            </Grid>) : ""
          }
        >
          {Object.keys(productDict).map((key) => (
            <MenuItem key={key} value={key}>
              <Grid container alignItems="center" spacing={1}>
                <Grid item><Box component="img" src={productLogo[key]} alt={key} sx={{ width: 124, height: 14, objectFit: "contain" }} /></Grid>
                <Grid item>{key}</Grid>
              </Grid>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Mailing list selection */}
          
      <Box sx={{ width: "100%", display: "flex", gap: 2, alignItems: "center" , my:2}}>
  {/* Left: Mailing list (50%) */}
  <FormControl sx={{ flex: "1 1 50%" }}>
    <InputLabel id="mailing-list-label">Select Mailing List</InputLabel>
    <Select
      labelId="mailing-list-label"
      value={selectedListKey}
      label="Select Mailing List"
      onChange={(e) => setSelectedListKey(e.target.value)}
    >
      {mailingLists.map((list) => (
        <MenuItem key={list.listkey} value={list.listkey}>
          {list.listname} ({list.noofcontacts} contacts)
        </MenuItem>
      ))}
    </Select>
  </FormControl>

  {/* Right: Time picker (50%) */}
  <Box sx={{ flex: "1 1 50%" }}>
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {/* use renderInput for broad compatibility */}
      <TimePicker
        label="Base Send Time (HH:mm)"
        value={sendTime}
        onChange={setSendTime}
        sx={{width:"100%"}}
        renderInput={(params) => <TextField {...params} />}
      />
    </LocalizationProvider>
  </Box>
</Box>


      {/* Multiple schedule steps */}
      {schedules.map((sched, index) => (
        
        <Box key={index} sx={{ p: 2, border: '1px solid #ddd', borderRadius: 2, widt:'100%' , my:2, mb: 3}}>
          <Grid container spacing={2} alignItems="center" justifyContent="space-between" sx={{width:"100%",flexWrap:"nowrap"}}>

            <Grid item  sx={{ flex: "0 0 7%" }}>
              <Typography>Step {sched.step}</Typography>
            </Grid>

            <Grid item sx={{ flex: "0 0 20%" }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Send Date"
                  value={sched.sendDate}
                  onChange={(date) => handleScheduleChange(index, 'sendDate', date)}
                  slotProps={{ textField: { fullWidth: true, size: "small" } }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item sx={{ flex: "0 0 45%" }}>
              <TextField
                label="Extra Email Prompt"
                value={sched.extraPrompt}
                onChange={(e) => handleScheduleChange(index, 'extraPrompt', e.target.value)}
                fullWidth multiline minRows={1} size="small"
              />
            </Grid>

            <Grid item sx={{ flex: "0 0 22%" }}>
              <FormControl fullWidth size='small'>
                <InputLabel>Email Type</InputLabel>
                <Select value={sched.emailType} label="Email Type" onChange={(e) => handleScheduleChange(index, 'emailType', e.target.value)}>
                  {emailTypes.map((type) => (<MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item sx={{ flex: "0 0 4%", textAlign: "center" }}>
              {schedules.length > 1 && <IconButton onClick={() => handleRemoveSchedule(index)}><Remove /></IconButton>}
            </Grid>
          </Grid>
        </Box>
      
      ))}



      <Button variant="outlined" startIcon={<Add />} onClick={handleAddSchedule} sx={{ mt: 2 }}>Add Schedule Step</Button>

      <Button
        variant="contained"
        color="primary"
        onClick={handleGenerateAllTemplates}
        sx={{ mt: 2 , ml:2, width:"420px"}}
        disabled={loading}
      >
        {loading ? <CircularProgress size={24} /> : 'Generate Templates'}
      </Button>
</>

}


      {/* Render editable templates if generated */}
      {generatedTemplates.length > 0 && (
        <TemplateEditorList
          templates={generatedTemplates}
          setTemplates={setGeneratedTemplates}
          selectedProduct={selectedProduct}
          selectedListKey={selectedListKey}
          onReset={resetForm}
        />
      )}
    </Box>
  );
};

const TemplateEditorList = ({ templates, setTemplates, selectedProduct, selectedListKey, onReset }) => {
  const [submitLoading, setSubmitLoading] = useState(false);

  const handleChange = (index, field, value) => {
    const updated = [...templates];
    updated[index][field] = value;
    setTemplates(updated);
  };

  // const handleSubmitAll = async () => {
  //   setSubmitLoading(true);
  //   try {
  //     const updated = [...templates];
  
  //     for (let i = 0; i < templates.length; i++) {
  //       const t = templates[i];
  
  //       // 1) Create campaign
  //       const resp = await axios.post(`${API_BASE_URL}/emailgenerator/createcampaign`, {
  //         product_name: selectedProduct,
  //         selectedMailingListKey: selectedListKey,
  //         templateName: t.templateName,
  //         subject: t.subject,
  //         htmlBody: t.htmlBody,
  //       });
  
  //       const campaignKey = resp?.data?.campaignKey || resp?.data?.campaign_key || resp?.data?.campaignkey;
  //       updated[i].campaignKey = campaignKey;
  
  //       // 2) Prepare schedule payload only if we have a date (t.sendDate)
  //       if (campaignKey && t.sendDate) {
  //         let dt = t.sendDate; // Dayjs object
  
  //         // merge time (use per-template sendTime if provided)
  //         if (t.sendTime && typeof t.sendTime.hour === "function") {
  //           dt = dt.hour(t.sendTime.hour()).minute(t.sendTime.minute());
  //         } else {
  //           // default time if not provided
  //           dt = dt.hour(9).minute(0);
  //         }
  
  //         const mm = String(dt.month() + 1).padStart(2, "0");
  //         const dd = String(dt.date()).padStart(2, "0");
  //         const yyyy = dt.year();
  //         const scheduledate = `${mm}/${dd}/${yyyy}`;
  
  //         let hour = dt.hour();
  //         const minute = dt.minute();
  //         const am_pm = hour >= 12 ? "PM" : "AM";
  //         if (hour > 12) hour -= 12;
  //         if (hour === 0) hour = 12;
  
  //         const payload = {
  //           campaign_key: campaignKey,
  //           scheduledate,
  //           schedulehour: hour,
  //           scheduleminute: minute,
  //           am_pm,
  //           sendingTZ: "Asia/Kolkata",
  //           istimewarp: false,
  //         };
  
  //         try {
  //           const schedResp = await scheduleCampaign(payload);
  //           updated[i].scheduled = true;
  //           updated[i].scheduleResponse = schedResp;
  //         } catch (schedErr) {
  //           updated[i].scheduled = false;
  //           updated[i].scheduleError = schedErr?.message || String(schedErr);
  //           console.error("Scheduling failed for", campaignKey, schedErr);
  //         }
  //       } else {
  //         updated[i].scheduled = false;
  //         updated[i].scheduleError = "No sendDate provided";
  //       }
  //     }
  
  //     setTemplates(updated);
  //     toast.success("All campaigns created and scheduled where possible!");
  //   } catch (err) {
  //     console.error("Create/schedule error:", err);
  //     toast.error("Failed to create or schedule campaigns: " + (err.message || err));
  //   } finally {
  //     setSubmitLoading(false);
  //   }
  // };
  

  const handleSubmitAll = async () => {
    setSubmitLoading(true);
    try {
      const updated = [...templates];
  
      for (let i = 0; i < templates.length; i++) {
        const t = templates[i];
  
        // 1️⃣ Create campaign
        const resp = await axios.post(`${API_BASE_URL}/emailgenerator/createcampaign`, {
          product_name: selectedProduct,
          selectedMailingListKey: selectedListKey,
          templateName: t.templateName,
          subject: t.subject,
          htmlBody: t.htmlBody,
        });
  
        const campaignKey = resp?.data?.campaignKey || resp?.data?.campaign_key || resp?.data?.campaignkey;
        updated[i].campaignKey = campaignKey;
  
        // 2️⃣ Schedule campaign if date exists
        if (campaignKey && t.sendDate) {
          let dt = t.sendDate;
  
          // Merge send time if available
          if (t.sendTime && typeof t.sendTime.hour === "function") {
            dt = dt.hour(t.sendTime.hour()).minute(t.sendTime.minute());
          } else {
            dt = dt.hour(9).minute(0);
          }
  
          const mm = String(dt.month() + 1).padStart(2, "0");
          const dd = String(dt.date()).padStart(2, "0");
          const yyyy = dt.year();
          const scheduledate = `${mm}/${dd}/${yyyy}`;
  
          let hour = dt.hour();
          const minute = dt.minute();
          const am_pm = hour >= 12 ? "PM" : "AM";
          if (hour > 12) hour -= 12;
          if (hour === 0) hour = 12;
  
          const payload = {
            campaign_key: campaignKey,
            scheduledate,
            schedulehour: hour,
            scheduleminute: minute,
            am_pm,
            sendingTZ: "Asia/Kolkata",
            istimewarp: false,
          };
  
          try {
            const schedResp = await scheduleCampaign(payload);
            updated[i].scheduled = true;
            updated[i].scheduleResponse = schedResp;
          } catch (schedErr) {
            updated[i].scheduled = false;
            updated[i].scheduleError = schedErr?.message || String(schedErr);
            console.error("Scheduling failed for", campaignKey, schedErr);
          }
        } else {
          updated[i].scheduled = false;
          updated[i].scheduleError = "No sendDate provided";
        }
      }
  
      // ✅ Update state
      setTemplates(updated);
  
      // ✅ Show success toast
      alert("All campaigns created and scheduled!");
  

      // ✅ Reset entire UI (handled by parent)
      onReset();


    } catch (err) {
      console.error("Create/schedule error:", err);
      toast.error("Failed to create or schedule campaigns: " + (err.message || err));
    } finally {
      setSubmitLoading(false);
    }
  };
  
  
  return (
    <Box p={2} sx={{minHeight:"70vh"}}>
      {templates.map((t, index) => (
  <Accordion key={index} sx={{my:2, mb:4}}>
  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
  <Typography variant="subtitle1">
  {`Product: ${t.product_name} | Step: ${t.step} | Type: ${t.emailType} | Send: ${t.sendDate ? t.sendDate.format('YYYY-MM-DD') : ''} ${t.sendTime ? t.sendTime.format('HH:mm') : ''}`}
</Typography>

  </AccordionSummary>
  <AccordionDetails>
  <Box mb={3}>
    <TextField
      fullWidth
      label="Campaign Template Name"
      value={t.templateName}
      onChange={(e) => handleChange(index, "templateName", e.target.value)}
      margin="normal"
    />
    <TextField
      fullWidth
      label="Email Subject"
      value={t.subject}
      onChange={(e) => handleChange(index, "subject", e.target.value)}
      margin="normal"
    />
    <TextField
      fullWidth
      label="Email Preheader"
      value={t.preheader}
      onChange={(e) => handleChange(index, "preheader", e.target.value)}
      margin="normal"
    />

    {/* ✅ Date & Time Editors */}
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <DatePicker
            label="Send Date"
            value={t.sendDate}
            onChange={(newDate) => handleChange(index, "sendDate", newDate)}
            renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TimePicker
            label="Send Time"
            value={t.sendTime}
            onChange={(newTime) => handleChange(index, "sendTime", newTime)}
            renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
          />
        </Grid>
      </Grid>
    </LocalizationProvider>

    {/* Email body editor */}
    <Editor
      license_key="gpl"
      tinymceScriptSrc={`${REACT_BASE_URL}/tinymce/tinymce.min.js`}
      value={t.htmlBody}
      init={{
        height: 400,
        menubar: false,
        toolbar:
          "undo redo | formatselect | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help",
        content_style: "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
      }}
      onEditorChange={(content) => handleChange(index, "htmlBody", content)}
    />
  </Box>
</AccordionDetails>

</Accordion>

      ))}

      <Button variant="contained" color="primary" fullWidth onClick={handleSubmitAll} disabled={submitLoading}>
        {submitLoading ? <CircularProgress size={24} /> : "Create Campaigns"}
      </Button>
    </Box>
  );
};

export default DripScheduleForm;
